'use client';

import type { ComponentProps } from 'react';
import { cn, Icon } from '@to/ui';
import {
  hasActiveFilters,
  type AgentFilterState,
  type AgentFilterSubsets,
  type AgentSortOption,
} from './agent-types';

/** Style the closed control consistently; keep the native keyboard/mobile picker. */
function FilterSelect({ className, style, children, ...props }: ComponentProps<'select'>) {
  return (
    <div className="relative">
      <select
        {...props}
        className={cn('w-full appearance-none pr-9', className)}
        style={{ ...style, appearance: 'none', WebkitAppearance: 'none' }}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-3 flex items-center"
        style={{ color: style?.color }}
      >
        <Icon name="keyboard_arrow_down" size={16} />
      </span>
    </div>
  );
}

interface AgentFiltersProps {
  /** The org's enabled filter subsets — the ONLY source of dropdown options. */
  subsets: AgentFilterSubsets;
  /** The committed filter/sort state (owned by the page). */
  value: AgentFilterState;
  /** Emits the FULL next state on every change (AND-compose). */
  onChange: (next: AgentFilterState) => void;
  /** The search input, rendered as the first cell of the primary row. */
  searchSlot?: React.ReactNode;
  busy?: boolean;
}

/**
 * Search-card interior for the Find Your Agent surface (v1 styling pass) — the
 * two control rows of the white card that overlaps the hero band:
 *
 *   Row 1 — `searchSlot` (flex-1) + Specialty + Area + "Sort:" selects.
 *   Row 2 — small Language select, the facet toggle chips (open houses /
 *           reviews), and "Clear all" when anything is active.
 *
 * Options derive ONLY from `subsets`; every control emits the full next
 * `AgentFilterState` so filters AND-compose. Every color via `--brand-*`.
 */
export function AgentFilters({
  subsets,
  value,
  onChange,
  searchSlot,
  busy = false,
}: AgentFiltersProps) {
  const active = hasActiveFilters(value);

  // ── Shared inline styles ──────────────────────────────────────────────────
  const selectStyle: React.CSSProperties = {
    backgroundColor: 'var(--brand-card-bg)',
    borderColor: 'var(--brand-input-border)',
    color: 'var(--brand-text)',
  };

  function chipStyle(on: boolean): React.CSSProperties {
    return on
      ? { backgroundColor: 'var(--brand-primary)' }
      : { backgroundColor: 'var(--brand-surface-subtle)', color: 'var(--brand-text-muted)' };
  }

  return (
    <div className="flex flex-col gap-3" aria-busy={busy}>
      {/* ── Row 1: search + primary selects ───────────────────────────────── */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        {searchSlot && <div className="min-w-0 flex-1">{searchSlot}</div>}

        <FilterSelect
          disabled={busy}
          aria-label="Specialty"
          value={value.specialtyId}
          onChange={(e) => onChange({ ...value, specialtyId: e.target.value })}
          className="h-11 cursor-pointer rounded-xl border pl-3 py-2.5 text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 sm:min-w-[150px]"
          style={selectStyle}
        >
          <option value="">All Specialties</option>
          {subsets.specialties.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          disabled={busy}
          aria-label="Service Area"
          value={value.serviceAreaId}
          onChange={(e) => onChange({ ...value, serviceAreaId: e.target.value })}
          className="h-11 cursor-pointer rounded-xl border pl-3 py-2.5 text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 sm:min-w-[130px]"
          style={selectStyle}
        >
          <option value="">All Areas</option>
          {subsets.serviceAreas.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          disabled={busy}
          aria-label="Sort"
          value={value.sort}
          onChange={(e) =>
            onChange({ ...value, sort: e.target.value as AgentSortOption })
          }
          className="h-11 cursor-pointer rounded-xl border pl-3 py-2.5 text-sm leading-5 focus-visible:outline-none focus-visible:ring-2 sm:min-w-[170px]"
          style={selectStyle}
        >
          <option value="recommended">Sort: Recommended</option>
          <option value="name">Sort: Name</option>
        </FilterSelect>
      </div>

      {/* ── Row 2: language + facet chips + clear ─────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          disabled={busy}
          aria-label="Language"
          value={value.languageId}
          onChange={(e) => onChange({ ...value, languageId: e.target.value })}
          className="h-9 cursor-pointer rounded-lg border pl-3 py-1.5 text-xs leading-4 focus-visible:outline-none focus-visible:ring-2"
          style={{
            backgroundColor: 'var(--brand-surface-subtle)',
            borderColor: 'var(--brand-input-border)',
            color: 'var(--brand-text-muted)',
          }}
        >
          <option value="">Language</option>
          {subsets.languages.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </FilterSelect>

        <button
          type="button"
          disabled={busy}
          aria-pressed={value.hasOpenHouses}
          onClick={() =>
            onChange({ ...value, hasOpenHouses: !value.hasOpenHouses })
          }
          className={cn(
            'min-h-11 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
            value.hasOpenHouses && 'text-white',
          )}
          style={chipStyle(value.hasOpenHouses)}
        >
          Has Open Houses
        </button>
        <button
          type="button"
          disabled={busy}
          aria-pressed={value.hasReviews}
          onClick={() => onChange({ ...value, hasReviews: !value.hasReviews })}
          className={cn(
            'min-h-11 cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
            value.hasReviews && 'text-white',
          )}
          style={chipStyle(value.hasReviews)}
        >
          Has Reviews
        </button>

        {active && (
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              onChange({
                ...value,
                specialtyId: '',
                languageId: '',
                serviceAreaId: '',
                hasOpenHouses: false,
                hasReviews: false,
              })
            }
            className="ml-1 text-xs font-medium underline underline-offset-2 transition-colors"
            style={{ color: 'var(--brand-text-muted)' }}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
